import { useEffect, useState, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MdAddCall, MdKeyboardArrowDown, MdVolumeUp } from 'react-icons/md'
import { formatSeconds } from '../../utils/dateParsing'
import { closeModal, openModal } from '../../redux/reducers/modalSlice'
import initTwilioVoice from '../../lib/initTwilioVoice'
import toastService from '../../services/toastService'

import avatar from '../../assets/images/defaultAvatar.svg'
import Dialer from '../../pages/dialer/Dialer'
import CallContactPicker from '../CallContacts/CallContactPicker'
import MergeCallPicker from './MergeCallPicker'
import { ReactComponent as MuteOff } from '../../assets/images/micOff.svg'
import { ReactComponent as MuteOn } from '../../assets/images/micOn.svg'
import { ReactComponent as PlussOff } from '../../assets/images/plusOff.svg'
import { ReactComponent as TransferOff } from '../../assets/images/transferOff.svg'
import { ReactComponent as MergeOff } from '../../assets/images/mergeOff.svg'
import { ReactComponent as HoldOff } from '../../assets/images/holdOff.svg'
import { ReactComponent as HoldOn } from '../../assets/images/holdOn.svg'
import { ReactComponent as ReacordingOff } from '../../assets/images/recordingOff.svg'
import { ReactComponent as ReacordingOn } from '../../assets/images/recordingOn.svg'
import { ReactComponent as EndCall } from '../../assets/images/callEnd.svg'
import { ReactComponent as DialerOff } from '../../assets/images/dialerOff.svg'
import CustomIcon from '../CustomIcon/CustomIcon'
import {
  removeCallOnHold,
  removeCallSlot,
  selectCurrentCall,
  selectIsInCall,
  selectIsInVideoCall,
  setCurrentCall,
  setIsInCall,
  updateCurrentCall,
  selectCallSlot,
  setIsOnHold,
  selectIsOnHold,
  selectIsConference,
  selectIsLocalHold,
  selectCallStartedAt,
  setCallStartedAt,
  setCallSlot,
  selectHeldLocalCalls,
} from '../../redux/reducers/callSlice'
import {
  useRecordCallMutation,
  useStopRecordingCallMutation,
  useAddNewCallLineMutation,
  useTransferNewCallMutation,
  useTransferConferenceCallLineMutation,
  useMergeConferenceCallOnHoldMutation,
  useMergeTwoCallsMutation,
} from '../../api/callsApiSlice'

import { useDispatch, useSelector } from 'react-redux'
import { CUSTOM_EVENT_TYPES } from '../../constants/matrixConst'

import styles from './InCall.module.scss'
import { selectCurrentId } from '../../redux/reducers/authSlice'
import { logAction, logResult } from '../../utils/callDebug'

// OSC username when the contact has an AirTouch account, otherwise the number (iOS inCallContact)
const getContactTarget = (contact) => contact?.endpoint?.username || contact?.number

const Call = () => {
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [callData, setCallData] = useState(null)
  const [outputDevices, setOutputDevices] = useState([])
  const [selectedOutput, setSelectedOutput] = useState('default')
  const isInCall = useSelector(selectIsInCall)
  const callSlots = useSelector(selectCallSlot)
  const currentCall = useSelector(selectCurrentCall)
  const userId = useSelector(selectCurrentId)
  const isOnHold = useSelector(selectIsOnHold)
  const isLocalHold = useSelector(selectIsLocalHold)
  const isConference = useSelector(selectIsConference)
  const callStartedAt = useSelector(selectCallStartedAt)
  const heldLocalCalls = useSelector(selectHeldLocalCalls)

  const [recordCall] = useRecordCallMutation()
  const [stopRecordingCall] = useStopRecordingCallMutation()
  const [addNewCallLine] = useAddNewCallLineMutation()
  const [transferNewCall] = useTransferNewCallMutation()
  const [transferConferenceCallLine] = useTransferConferenceCallLineMutation()
  const [mergeConferenceCallOnHold] = useMergeConferenceCallOnHoldMutation()
  const [mergeTwoCalls] = useMergeTwoCallsMutation()

  const callsOnHold = callSlots?.filter((call) => call?.isOnHold)

  const location = useLocation()
  const dispatch = useDispatch()
  const { state } = location
  const navigate = useNavigate()
  const isInVideCall = useSelector(selectIsInVideoCall)

  const callerName = useMemo(() => {
    if (!currentCall?.participants?.length) return currentCall?.caller || ''

    const name =
      'Merged call: ' +
      currentCall.participants
        .map((participant) => {
          if (participant?.caller) return participant.caller
          return participant.callerId
        })
        .join('-')
    return name
  }, [currentCall?.caller, currentCall?.participants])

  useEffect(() => {
    // `resume` = user came back from Home while the call is still running (Close button)
    if (!state?.params && !state?.incommingCall && !state?.resume) {
      initTwilioVoice.handleReject()
      if (!isInVideCall) navigate('/')
    }
  }, [isInVideCall, navigate, state?.incommingCall, state?.params, state?.resume])

  useEffect(() => {
    const handleCallEnded = (event) => {
      dispatch(closeModal())
      const hasNewCall = event?.detail?.message?.hasNewCall
      const hasCallOnHold = event?.detail?.message?.hasCallOnHold
      const callerId = event?.detail?.message?.callerId
      if (callerId) {
        const currentCallIds = currentCall?.callerId?.split('#') || []
        const shouldUpdateCurrentCall = currentCallIds.find((id) => id === callerId)
        if (currentCallIds.length > 1 && shouldUpdateCurrentCall) {
          dispatch(updateCurrentCall({ callerId }))
        }
        dispatch(removeCallOnHold({ callerId }))
        dispatch(removeCallSlot({ callerId }))
        if (currentCall?.callerId === callerId) {
          dispatch(setCurrentCall(null))
        }
      }
      if (!hasNewCall && !hasCallOnHold) {
        const hasRemainingCalls = initTwilioVoice.handleReject()
        if (!hasRemainingCalls && !isInVideCall) navigate('/')
        return
      }
    }

    document.addEventListener(CUSTOM_EVENT_TYPES.call_ended, handleCallEnded)
    return () => {
      document.removeEventListener(CUSTOM_EVENT_TYPES.call_ended, handleCallEnded)
    }
  }, [currentCall?.callerId, dispatch, isInVideCall, navigate])

  useEffect(() => {
    const handleMuteCall = () => {
      setIsMuted(true)
    }
    const handleUnmuteCall = () => {
      setIsMuted(false)
    }
    document.addEventListener(CUSTOM_EVENT_TYPES.muteCall, handleMuteCall)
    document.addEventListener(CUSTOM_EVENT_TYPES.unmuteCall, handleUnmuteCall)
    return () => {
      document.removeEventListener(CUSTOM_EVENT_TYPES.muteCall, handleMuteCall)
      document.removeEventListener(CUSTOM_EVENT_TYPES.unmuteCall, handleUnmuteCall)
    }
  }, [])

  // Duration is derived from `callStartedAt` in Redux so it survives Close -> Home -> resume
  useEffect(() => {
    const handleAnsweredCall = () => {
      dispatch(setIsInCall(true))
      dispatch(setCallStartedAt(Date.now()))
    }
    if (state?.incommingCall && !callStartedAt) {
      handleAnsweredCall()
    }
    document.addEventListener(CUSTOM_EVENT_TYPES.answered_call, handleAnsweredCall)
    return () => {
      document.removeEventListener(CUSTOM_EVENT_TYPES.answered_call, handleAnsweredCall)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state?.incommingCall])

  useEffect(() => {
    if (!callStartedAt) {
      setDuration(0)
      return
    }
    const tick = () => setDuration(Math.floor((Date.now() - callStartedAt) / 1000))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [callStartedAt])

  useEffect(() => {
    if (!isInCall) return
    setOutputDevices(initTwilioVoice.getOutputDevices())
    setSelectedOutput(initTwilioVoice.getSelectedOutputDeviceId())
    const audio = initTwilioVoice.device?.audio
    const refresh = () => setOutputDevices(initTwilioVoice.getOutputDevices())
    audio?.on('deviceChange', refresh)
    return () => {
      audio?.off('deviceChange', refresh)
    }
  }, [isInCall])

  useEffect(() => {
    const hendlePutOnHold = () => {
      dispatch(setIsOnHold(true))
    }

    const hendleActiveInConference = () => {
      dispatch(setIsOnHold(false))
    }
    document.addEventListener(CUSTOM_EVENT_TYPES.put_on_hold, hendlePutOnHold)
    document.addEventListener(CUSTOM_EVENT_TYPES.active_in_conference, hendleActiveInConference)
    return () => {
      document.removeEventListener(CUSTOM_EVENT_TYPES.put_on_hold, hendlePutOnHold)
      document.removeEventListener(
        CUSTOM_EVENT_TYPES.active_in_conference,
        hendleActiveInConference,
      )
    }
  }, [dispatch])

  const handleHangup = () => {
    logAction('End call')
    const hasRemainingCalls = initTwilioVoice.handleReject()
    if (!hasRemainingCalls) navigate('/')
  }

  const handleShowDialer = () => {
    logAction('Keypad')
    dispatch(
      openModal({
        show: true,
        content: <Dialer />,
        size: 'md',
        closeOnOutsideClick: false,
      }),
    )
  }

  const handleRecording = async () => {
    logAction(callData ? 'Record (stop)' : 'Record (start)')
    let action
    let props = { accountId: userId }
    if (callData && callData?.callSid && callData?.recordingSid) {
      action = stopRecordingCall
      props = {
        ...props,
        ...callData,
      }
    } else {
      action = recordCall
    }

    const { data } = await action(props)
    if (data?.isSuccess && data?.callSid && data?.recordingSid) {
      setCallData({ callSid: data.callSid, recordingSid: data.recordingSid })
    } else {
      setCallData(null)
    }
  }

  const closePicker = useCallback(() => dispatch(closeModal()), [dispatch])

  const openPicker = useCallback(
    (content) => {
      dispatch(
        openModal({
          show: true,
          content,
          size: 'md',
          closeOnOutsideClick: true,
          showCloseIcon: false,
        }),
      )
    },
    [dispatch],
  )

  // Add: dial a third party into the current call (backend turns it into a conference)
  const handleAddParticipant = async (contact) => {
    const to = getContactTarget(contact)
    const inProgressCallSid = initTwilioVoice.getActiveCallSid()
    logAction('Add → contact picked', {
      to,
      inProgressCallSid,
      contact,
      currentCall,
      rawCallSid: initTwilioVoice.getCallSid(),
    })
    if (!to || !inProgressCallSid) {
      toastService.show('error', 'Cannot add participant to this call')
      return
    }
    closePicker()
    const response = await addNewCallLine({ accountId: userId, to, inProgressCallSid })
    logResult('addNewCallLine', response)
    const { data } = response
    if (!data?.isSuccess) {
      toastService.show('error', 'Error adding participant')
      return
    }
    dispatch(
      setCallSlot({ caller: contact?.name || to, callerId: to, participants: [], isOnHold: false }),
    )
    toastService.show('success', `Calling ${contact?.name || to}`)
  }

  const handleShowAdd = () => {
    logAction('Add (opening contact picker)')
    openPicker(
      <CallContactPicker
        title='Add to call'
        onSelect={handleAddParticipant}
        onClose={closePicker}
      />,
    )
  }

  // Transfer: blind transfer of the current line to the chosen contact, then leave (iOS: dismiss)
  const handleTransfer = async (contact) => {
    const to = getContactTarget(contact)
    const callerId = currentCall?.callerId
    // Must be the leg the backend knows (parent leg). Without it the backend falls back to a
    // callLine lookup by callerId that does not match an incoming PSTN-prefixed leg — it then
    // silently does nothing and still answers { isSuccess: true }.
    const incomingCallSid = currentCall?.incomingCallSid || initTwilioVoice.getActiveCallSid()
    logAction('Transfer → contact picked', {
      to,
      callerId,
      isConference,
      incomingCallSid,
      fromCurrentCall: currentCall?.incomingCallSid,
      fromLiveCall: initTwilioVoice.getActiveCallSid(),
      contact,
      currentCall,
    })
    if (!to || !callerId) {
      toastService.show('error', 'Cannot transfer this call')
      return
    }
    closePicker()
    const response = isConference
      ? await transferConferenceCallLine({ accountId: userId, callerId, to })
      : await transferNewCall({ accountId: userId, callerId, to, incomingCallSid })
    logResult(isConference ? 'transferConferenceCallLine' : 'transferNewCall', response)
    if (!response?.data?.isSuccess) {
      toastService.show('error', 'Error transfer call')
      return
    }
    if (!callsOnHold?.length) {
      const hasRemainingCalls = initTwilioVoice.handleReject()
      if (!hasRemainingCalls) navigate('/')
    } else {
      dispatch(removeCallSlot({ callerId }))
      dispatch(setCurrentCall(null))
    }
  }

  const handleShowTransfer = () => {
    logAction('Transfer (opening contact picker)')
    openPicker(
      <CallContactPicker title='Transfer to' onSelect={handleTransfer} onClose={closePicker} />,
    )
  }

  // Merge a second *local* call (iOS mergeBtnClicked): server joins both legs, then we drop
  // our leg of the active call and stay on the remaining one.
  const handleMergeLocalCall = async (call) => {
    closePicker()
    const callSid1 = initTwilioVoice.getActiveCallSid()
    const callSid2 = call.callSid
    logAction('Merge → local call picked', { callSid1, callSid2, call })
    if (!callSid1 || !callSid2) {
      toastService.show('error', 'Cannot merge: call is not connected yet')
      return
    }
    const response = await mergeTwoCalls({ accountId: userId, callSid1, callSid2 })
    logResult('mergeTwoCalls', response)
    const { data } = response
    if (!data?.isSuccess) {
      toastService.show('error', 'Error merging calls')
      return
    }
    const hasRemainingCalls = initTwilioVoice.handleReject()
    if (!hasRemainingCalls) navigate('/')
  }

  // Merge: join a call that is on hold into the active one (same as Dialer's handleMergeCall)
  const handleMerge = async (call) => {
    if (call.isLocal) return handleMergeLocalCall(call)
    closePicker()
    logAction('Merge → conference call picked', { callerId: call.callerId, call })
    const response = await mergeConferenceCallOnHold({ accountId: userId, callerId: call.callerId })
    logResult('mergeConferenceCallOnHold', response)
    const { data } = response
    if (!data?.isSuccess) {
      toastService.show('error', 'Error merging calls')
      return
    }
    const mergeCallData = {
      callerId: `${call.callerId}#${currentCall.callerId}`,
      participants: currentCall?.participants?.length
        ? [call, ...currentCall.participants]
        : [currentCall, call],
      caller: 'Merge call',
    }
    dispatch(removeCallOnHold(call))
    dispatch(removeCallSlot(currentCall))
    dispatch(setCallSlot(mergeCallData))
    dispatch(removeCallSlot({ ...call, action: 'merge' }))
    dispatch(setCurrentCall(mergeCallData))
  }

  // A blind transfer redirects the leg the backend knows. For an incoming call that is the
  // caller's leg (correct); for a plain outgoing call it is our own leg, so redirecting it would
  // drop the other party instead of transferring them — the backend never stores the child leg.
  // Conference lines go through transferConferenceCallLine, which works either way.
  const canTransfer = isConference || currentCall?.direction === 'incoming'

  const mergeCandidates = [
    ...heldLocalCalls.map((call) => ({ ...call, isLocal: true })),
    ...(callsOnHold || []),
  ]

  const handleShowMerge = () => {
    logAction('Merge (opening picker)', { mergeCandidates })
    openPicker(
      <MergeCallPicker calls={mergeCandidates} onMerge={handleMerge} onClose={closePicker} />,
    )
  }

  // New call while in a call: the active one goes on local hold (iOS: Close -> Home -> dial)
  const handleNewCall = async (contact) => {
    const to = getContactTarget(contact)
    logAction('New call → contact picked', { to, contact })
    if (!to) return
    closePicker()
    const { token } = await initTwilioVoice.getToken()
    if (token) await initTwilioVoice.updateToken(token)
    await initTwilioVoice.connectDevice({
      To: to,
      caller: contact?.name || to,
      callerId: to,
      organizationName: contact?.organizationName,
    })
  }

  const handleShowNewCall = () => {
    logAction('New call (opening contact picker)')
    openPicker(
      <CallContactPicker title='New call' onSelect={handleNewCall} onClose={closePicker} />,
    )
  }

  const handleToggleHold = () => {
    logAction(isLocalHold ? 'Hold (resume)' : 'Hold (put on hold)')
    isLocalHold ? initTwilioVoice.resumeCall() : initTwilioVoice.holdCall()
  }

  const handleOutputChange = async (e) => {
    const deviceId = e.target.value
    logAction('Speaker (output device changed)', { deviceId })
    if (await initTwilioVoice.setOutputDevice(deviceId)) setSelectedOutput(deviceId)
  }

  // Close: back to Home with the call still running; Home shows a "Return to call" bar
  const handleClose = () => {
    logAction('Close (back to app, call keeps running)')
    navigate('/')
  }

  return (
    <div className={styles.callWrapper}>
      <div className={styles.call}>
        <div className={styles.topBar}>
          <span className={styles.closeBtn} onClick={handleClose} title='Back to app'>
            <MdKeyboardArrowDown />
          </span>
        </div>
        <div className={styles.onHoldWrapper}>
          {heldLocalCalls.map((call) => (
            <div
              className={`${styles.onHold} ${styles.clickable}`}
              key={call.id}
              onClick={() => {
                logAction('Swap to held call', { call })
                initTwilioVoice.switchToCall(call.id)
              }}
              title='Switch to this call'
            >
              <span className={styles.name}>{call.caller || call.callerId}</span>
              <span className={styles.holdBnt}>HOLD · tap to swap</span>
            </div>
          ))}
          {callsOnHold?.length
            ? callsOnHold?.map((call) => {
                if (!call?.callerId) return null
                return (
                  <div className={styles.onHold} key={call.callerId}>
                    <span className={styles.name}>{call.caller}</span>
                    <span className={styles.holdBnt}>HOLD</span>
                  </div>
                )
              })
            : null}
        </div>
        <div className={styles.contactInfo}>
          <img src={avatar} alt='' />
          {isOnHold || isLocalHold ? <span className={styles.callingInfo}>On hold</span> : null}
          {!currentCall ? (
            <span className={styles.name}>
              You have{' '}
              {callsOnHold?.length > 1
                ? `${callsOnHold?.length} calls `
                : `${callsOnHold?.length} call `}
              on hold
            </span>
          ) : (
            <>
              <span className={styles.name}>{callerName || ''}</span>
              {state?.organizationName ? (
                <span className={styles.callingInfo}>({state.organizationName})</span>
              ) : null}
              <span className={styles.callingInfo}>
                {duration === 0 ? 'Calling...' : formatSeconds(duration)}
              </span>
            </>
          )}
        </div>
        {isInCall && (
          <div className={styles.btnWrapper}>
            {isMuted ? (
              <CustomIcon
                icon={<MuteOff className={styles.icon} />}
                label='Mute'
                onClick={() => {
                  logAction('Unmute')
                  initTwilioVoice.unmuteCall()
                }}
              />
            ) : (
              <CustomIcon
                icon={<MuteOn className={styles.icon} />}
                label='Mute'
                onClick={() => {
                  logAction('Mute')
                  initTwilioVoice.muteCall()
                }}
              />
            )}
            {callData ? (
              <CustomIcon
                icon={<ReacordingOn className={styles.icon} />}
                label='Record'
                onClick={handleRecording}
              />
            ) : (
              <CustomIcon
                icon={<ReacordingOff className={styles.icon} />}
                label='Record'
                onClick={handleRecording}
              />
            )}
            <CustomIcon
              icon={
                isLocalHold ? (
                  <HoldOn className={styles.icon} />
                ) : (
                  <HoldOff className={styles.icon} />
                )
              }
              label='Hold'
              onClick={handleToggleHold}
            />
            <CustomIcon
              icon={<PlussOff className={styles.icon} />}
              label='Add'
              onClick={handleShowAdd}
            />
            {canTransfer ? (
              <CustomIcon
                icon={<TransferOff className={styles.icon} />}
                label='Transfer'
                onClick={handleShowTransfer}
              />
            ) : null}
            {mergeCandidates.length ? (
              <CustomIcon
                icon={<MergeOff className={styles.icon} />}
                label='Merge'
                onClick={handleShowMerge}
              />
            ) : null}
            <CustomIcon
              icon={<MdAddCall className={styles.icon} />}
              label='New call'
              onClick={handleShowNewCall}
            />
            {outputDevices.length > 1 ? (
              <div className={styles.speaker}>
                <MdVolumeUp className={styles.speakerIcon} />
                <select value={selectedOutput} onChange={handleOutputChange}>
                  {outputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || device.deviceId}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        )}
        <div className={styles.callBtns}>
          <CustomIcon icon={<EndCall className={styles.icon} />} onClick={handleHangup} />
          {isInCall && (
            <CustomIcon icon={<DialerOff className={styles.icon} />} onClick={handleShowDialer} />
          )}
        </div>
      </div>
    </div>
  )
}

export default Call
